// Note: This is the main store for the entire application. It is used by all apps and components.
import Vue from 'vue'
import Vuex from 'vuex'
import axios from "axios"

// Helpers
import mainAPI from './mainAPI'
import mainMixins from './mainMixins'
import {DateTime as dt} from "luxon"
window.ROOT = { root: true } // used to dispatch actions/mutations from the root of the store

// Storage
import Cookies from 'js-cookie'
import VuexPersistence from 'vuex-persist'
import cookieStorage from './storage/cookieStorage'
import sessionStorage from './storage/sessionStorage'
import personalizationStorage from './storage/personalizationStorage'

// Factories
import envFactory from "./factories/envFactory"
import metaFactory from "./factories/metaFactory"
import ecommerceItemFactory from "./factories/ecommerceItemFactory"

// Stores
import BuyflowStore from './apps/Buyflow/store'
import PersonalizationStore from './personalization/store'
import OrderRemoteStore from './apps/OrderRemoteControl/store'
import LabelStore from './labelsStore'
import HighlightStore from './highlightStore'
import PromotionStore from './promotionStore'

import { ElementApiModule } from 'clockvine-vue'
import _ from "lodash";

// Utils
import rightOfEntry from './utils/rightOfEntry'

// Axios Config - 30 second timeout
axios.defaults.timeout = 30000

// Vue Config
Vue.use(Vuex)

export default new Vuex.Store({
    namespaced: true,
    plugins: [
        new VuexPersistence({
            storage: window.sessionStorage,
            reducer: state => (sessionStorage(state)),
        }).plugin,
        new VuexPersistence({
            key: 'personalization',
            restoreState: (key, storage) => { return JSON.parse(Cookies.get(key) || '{}') },
            saveState: (key, state, storage) => { return Cookies.set(key, JSON.stringify(state), { expires: 90 }) },
            reducer: state => (personalizationStorage(state)),
        }).plugin,
        new VuexPersistence({
            restoreState: (key, storage) => { return JSON.parse(Cookies.get(key) || '{}') },
            saveState: (key, state, storage) => { return Cookies.set(key, JSON.stringify(state)) },
            reducer: state => (cookieStorage(state)),
        }).plugin,
    ],
    state: () => ({
        env: envFactory(),
        meta: metaFactory(),
        ecommerceItem: ecommerceItemFactory(),
        csgEnvironment: 'PROD',
        clearlinkId: '',
        clearlinkOfferId: '',
        openOverlaysCount: 0, // the number of open overlays. scrolling is suppressed while this is > 0
        loadingQueue: 0,
        isLoading: false, // the global loading overlay which blocks user input, see LoadingOverlay
        loadingVisibility: true,
        // also blocks input, need to be dismissed explicitly, see NotificationsOverlay
        popup: {
            isVisible: false,
            type: null,
        },
        forcedMarket: {
            marketType: null,
            showPopup: false,
        },
        checkAvailabilityModal: {
            visible: false,
            routeToBuyflow: false, // true = routes to buyflow after addressing in, false = just hides the modal
            resetSelectedOffer: false,
            currentCustomerCheck: false,
            movingToAddress: false,
            hasSelectedGiftCard: false,
        },
        businessSelectPlanModal: {
            visible: false,
            termsValues: null,
            confirmation: false,
        },
        selectedOfferIsAvailable: true,
        marketOverrides: null,
        businessOffers: {},
        residentialOffers: [],
        selectedOffer: {
            name: '',
            selected: [],
            backupNames: [],
        },
        scrollPosition: null,
        scrollPositionLock: null,
        quickAddressInUsed: false,
        quickAddressInError: false,
        analyticsMarket: '',

        googleAPI: {
            hasLoaded: false,
            sessionTokenCreated: null,
            sessionToken: null,
            places: null,
            autocomplete: null,
        },

        giftCardEligible: false,
        showTvNotAvailableModal : true,
    }),

    getters: {
        getCurrentAddressAsString: (state) => (Vue, fullAddress = true) => {
            const m = state.meta
            if (m.line1 && fullAddress){
                return `${Vue.toPascalCase(m.line1)}${m.line2?.length? ' ' + Vue.toPascalCase(m.line2): ''}, ${Vue.toPascalCase(m.city)}, ${m.stateOrProvince} ${m.postalCode.substring(0,5)}`
            } else if (m.city && m.stateOrProvince && m.postalCode) {
                return `${Vue.toPascalCase(m.city)}, ${m.stateOrProvince}  ${m.postalCode.substring(0,5)}`
            }
            return ''
        },
        isPendingDisconnect: state => { // returns true if the user is in a pending disconnect state and the date is within 5 months
            const pd = state.meta.pendingDisconnect
            return (pd.hasPendingDisconnect && pd.date && dt.fromFormat(pd.date.split('T')[0], 'yyyy-LL-dd') <= dt.now().plus({ months: 5 }))
        },
        showOfferCards: (state) => {
            const m = state.meta
            if (!m || !state.residentialOffers || !Array.isArray(state.residentialOffers)) {
                return false;
            }
            return !!(m.line1 && m.postalCode && state.residentialOffers.length)
        },
        offerIdMatches: (state) => (feature) => {
            // offer_3665|use_secondary_market|buyflow_hide=tv:video:edits|buyflow_minimal=internet
            // if there is a feature flag that contains the keyword offer_ then we need to make sure the rest of the feature is reliant on this returning true
            if (feature.indexOf('offer_') > -1) {
                const items = feature.split('|')

                // check if the offer id matches the one in the feature flag
                for (let i in items){
                    if (items[i] === ('offer_' + state.meta.offerId)){
                        // found a match
                        return true
                    }
                }
                // no match found
                return false
            }
            // no need to match as there isn't an offer id in the feature
            return true
        },
        parseMetadata: (state, getters) => (data) => {
            const names = _.keys(_.groupBy(getters.offers, 'name'))
            return data.filter(product => names.indexOf(product.name) > -1)
        },
        filterAndMerge: (state, getters) => ({base, override}) => {
            return _.mergeWith(getters.filterByAudience(base), getters.filterByAudience(override), (objValue, srcValue) => {
                if (srcValue === null || srcValue?.length === 0) {
                    return objValue
                }
            })
        },
        filterByAudience: (state) => (data) => {
            return _.groupBy(data.map(product => {
                return {
                    ...product,
                    variants: _.last(product.variants?.filter(variant => {
                        switch(variant.audienceType){
                            case 'default':
                                return true
                            case 'marketType':
                                const markets = variant.audienceValue?.split(',')
                                return markets.indexOf(state.meta.marketType) > -1
                            case 'zipcode':
                                return (variant.audienceValue?.indexOf(state.meta.postalCode?.substring(0, 5)) > -1)
                            case 'offerId':
                                return (variant.audienceValue?.indexOf(state.meta.offerId) > -1)
                            case 'queryParameter':
                                const queryString = window.location.search;
                                const queryParameters = variant.audienceValue?.split(',')
                                return queryParameters.every(value => {
                                    const query = value.split('=')
                                    if(query.length > 1) {
                                        return new URLSearchParams(queryString).get(query[0]) === query[1]
                                    }
                                    return new URLSearchParams(queryString).has(query[0])
                                })
                            case 'vuexStore':
                                const vuex = variant.audienceValue?.split(',')
                                return vuex.every(value => {
                                    const data = value.split('=')
                                    if(data.length > 1) {
                                        return typeof state.meta[data[0]] !== 'undefined' && state.meta[data[0]] === data[1]
                                    }
                                    return typeof state.meta[data[0]] !== 'undefined'
                                })
                        }
                    })?? [])
                }
            }), 'name')
        },
        offers: (state) =>{
            if (!state.residentialOffers || !Array.isArray(state.residentialOffers)) {
                return [];
            }
            return JSON.parse(JSON.stringify(state.residentialOffers))
        },
        offersByType: (state, getters) => ({mergedMetadata, offerType, customType}) => {
            if (!getters.offers.length){
                return []
            }
            function mergeVariant(variant, meta, productName, additional = {}){
                return variant.map(v => {
                    return {
                        ...v,
                        ...meta?.variants?.customizations[productName]?.products[v.name] ?? {},
                        ...additional
                    }
                })
            }
            let offers = getters.offers
            let reordered = []
            if (offerType === 'custom'){
                if (customType?.length > 0){
                    customType.forEach(item => {
                        if (item.productName){
                            reordered.push(offers.find(o => o.name === item.productName))
                        }
                    })
                    if (reordered.length > 0){
                       offers = reordered
                    }
                }
            }

            return offers.map((offer, key) => {

                const meta = mergedMetadata[offer.name][0]?? {}
                const internet = meta?.variants?.customizations?.internet?.products[offer.name] ?? {}
                let variants = []
                let promoPrice = _.isArray(offer.credits) ? offer.promoPrice: _.round((offer.promoPrice + offer.credits.value),2)
                let filteredOfferType = offerType

                if (offerType === 'custom') {
                    filteredOfferType = (customType?.length > 0? customType[key]?.offerType: 'internet')
                }

                switch (filteredOfferType){
                    case 'internet':
                        console.log("internet", offer.promoPrice, offer.fullPrice);

                        return {
                            ...offer,
                            promoPrice,
                            ...internet,
                            variants,
                            hsdPromoPrice: offer.promoPrice,
                            hsdFullPrice: offer.fullPrice,
                            allData: offer.variants
                        }
                    case 'internet-equipment':
                        let eg = offer.variants?.equipment?.equipment_gateway ?? []

                        const wywOptions = offer.variants?.equipment?.equipment_wyw ?? [];

                        const twoExtendersOption = wywOptions.find(wyw => wyw.name === "WiFi Your Way -  2 WiFi 6 Extenders");
                        const selectedWyw = twoExtendersOption ?? wywOptions[0];
                        let ewyw = [selectedWyw];

                        variants = [
                            ...mergeVariant(eg, meta, 'equipment', { title: null }),
                            ...mergeVariant(ewyw, meta, 'equipment'),
                        ]
                        promoPrice = variants.reduce((acc, variant) => acc + variant.promoPrice, offer.promoPrice)
                        console.log("internet-equipment", offer.promoPrice, offer.fullPrice);
                        return {
                            ...offer,
                            promoPrice: _.round(_.isArray(offer.credits) ? promoPrice: promoPrice + offer.credits.value,2),
                            fullPrice: _.round(variants.reduce((a, v) => a + v.fullPrice, offer.fullPrice),2),
                            ...internet,
                            variants,
                            hsdPromoPrice: offer.promoPrice,
                            hsdFullPrice: offer.fullPrice,
                            title: variants.reduce((a, v) => v.title? a + ' + ' + v.title: a, internet.title ?? offer.title),
                            pricingDetails: [...variants, internet].find(v => v.pricingDetails)?.pricingDetails,
                            selected: variants.reduce((a, v) => _.merge(a, v.selected), offer.selected),
                            allData: offer.variants
                        }
                    case 'internet-tv':
                        let tv = offer.variants?.tv ?? []
                        let tvVar = tv?.find(tv => tv.variants !== null)?.variants?.tv_equipment ?? []
                        variants = [
                            ...mergeVariant(tv, meta, 'tv', { title: null }),
                            ...mergeVariant(tvVar, meta, 'tv'),
                        ]
                        promoPrice = variants.reduce((acc, variant) => acc + variant.promoPrice, offer.promoPrice)
                        let fullPrice = variants.reduce((acc, variant) => acc + variant.promoPrice, offer.fullPrice + 10)
                        console.log("internet-tv", offer.promoPrice, offer.fullPrice);
                        return {
                            ...offer,
                            promoPrice: _.round(_.isArray(offer.credits) ? promoPrice: promoPrice + offer.credits.value,2),
                            fullPrice: _.round(_.isArray(offer.credits) ? fullPrice: fullPrice + offer.credits.value,2),
                            ...internet,
                            variants,
                            hsdPromoPrice: offer.promoPrice,
                            hsdFullPrice: offer.fullPrice,
                            title: variants.reduce((a, v) => v.title? a + ' + ' + v.title: a, internet.title ?? offer.title),
                            pricingDetails: [...variants, internet].find(v => v.pricingDetails)?.pricingDetails,
                            gradientCallout: [...variants, internet].find(g => g.gradientCallout)?.gradientCallout,
                            previousPrice: [...variants, internet].find(g => g.previousPrice)?.previousPrice,
                            banner: [...variants, internet].find(g => g.banner)?.banner,
                            selected: _.merge(offer.selected, _.mapValues(_.groupBy(variants,v => Object.keys(v.selected)[0]), (v, k) => v.length === 1 ? v[0].selected[k]: v.map(v => v.selected[k]))),
                            allData: offer.variants
                        }
                    case 'internet-voice':
                        let voice = offer.variants?.voice.filter(v => v.fullPrice > 0) ?? []
                        variants = mergeVariant(voice, meta, 'voice')
                        promoPrice = variants.reduce((acc, variant) => acc + variant.promoPrice, offer.promoPrice)
                        console.log("internet-voice", offer.promoPrice, offer.fullPrice);
                        return {
                            ...offer,
                            promoPrice: _.round(_.isArray(offer.credits) ? promoPrice: promoPrice + offer.credits.value,2),
                            fullPrice: _.round(variants.reduce((a, v) => a + v.fullPrice, offer.fullPrice),2),
                            ...internet,
                            variants,
                            hsdPromoPrice: offer.promoPrice,
                            hsdFullPrice: offer.fullPrice,
                            title: variants.reduce((a, v) => v.title? a + ' + ' + v.title: a, internet.title ?? offer.title),
                            pricingDetails: [...variants, internet].find(v => v.pricingDetails)?.pricingDetails,
                            gradientCallout: [...variants, internet].find(g => g.gradientCallout)?.gradientCallout,
                            previousPrice: [...variants, internet].find(g => g.previousPrice)?.previousPrice,
                            banner: [...variants, internet].find(g => g.banner)?.banner,
                            selected: variants.reduce((a, v) => _.merge(a, v.selected), offer.selected),
                            allData: offer.variants
                        }
                }
            })
        },
        isFeatureSet: (state, getters) => (flag, features = null) => {
            const featureList = features || state.meta.features
            for(let i in featureList){
                if (featureList[i].indexOf(flag) > -1 && getters.offerIdMatches(featureList[i])) {
                    return true
                }
            }
            return false
        },
        useSecondaryMarket: (state, getters) => (features) => {
            return getters.isFeatureSet('use_secondary_market', features)
        },
        isFmf (state){ // First Month Free
            return !!(state.meta.features && state.meta.features.find(f => f === 'resi_fmf'))
        },
        fmfAmount (state, getters){ // First Month Free Amount
            if (getters.isFmf) {
                const offer = Object.values(getters.offers).find(o => o.name === state.Buyflow.cartData?.services?.internet[0].name)|| null
                return offer?.promoPrice? offer.promoPrice: ''
            }
            return ''
        },
        discountAmount (state, getters){
            if (!!(state.meta.features && state.meta.features.find(f => f.indexOf('promo_') > -1))) {
                let feature = state.meta.features.find(f => f.indexOf('promo_') > -1)

                if (feature.indexOf('promo_discount') > -1) {
                    return parseFloat(feature.split('_')[2] || '0')
                } else if (feature.indexOf('promo_tv_discount') > -1  && getters['Buyflow/tv/hasSelectedAService']) {
                    return parseFloat(feature.split('_')[3] || '0')
                } else if (feature.indexOf('promo_voice_discount') > -1 && getters['Buyflow/voice/hasSelected']()) {
                    return parseFloat(feature.split('_')[3] || '0')
                }
            }
            return 0
        },
        edpDiscountAmount (state, getters){
            if (!!(state.meta.features && state.meta.features.find(f => f.indexOf('edp_') > -1))) {
                const feature = state.meta.features?.filter(f => f.indexOf('edp_') > -1)|| []

                // if feature is not empty
                for (let i in feature){
                    if(getters.offerIdMatches(feature[i])){
                        const items = feature[i].split('|')
                        for (let i in items){
                            if(items[i].indexOf('edp_') > -1) {
                                // feature is active
                                if (items[i].indexOf('edp_discount') > -1) {
                                    return parseFloat(items[i].split('_')[2] || '0')
                                } else if (items[i].indexOf('edp_wifi_discount') > -1 && getters['Buyflow/internet/selectedEquipment'].name !== 'Gateway Lease') {
                                    const edp = items[i].split('_')
                                    return parseFloat(edp[edp?.length -1] || '0')
                                } else if (items[i].indexOf('edp_modem_discount') > -1 && getters['Buyflow/internet/selectedEquipment'].name === 'Gateway Lease') {
                                    const edp = items[i].split('_')
                                    return parseFloat(edp[edp?.length -1] || '0')
                                } else if (items[i].indexOf('edp_tv_discount') > -1  && getters['Buyflow/tv/hasSelectedAService']) {
                                    return parseFloat(items[i].split('_')[3] || '0')
                                } else if (items[i].indexOf('edp_voice_discount') > -1 && getters['Buyflow/voice/hasSelected']()) {
                                    return parseFloat(items[i].split('_')[3] || '0')
                                }
                            }
                        }
                    }
                }
            }
            return 0
        },
        isGwp (state, getters){ // Gift With Purchase
            return !!(state.meta.features && state.meta.features.find(f => f === 'resi_gwp') && getters['Buyflow/internet/selectedService']?.speed?.download >= 500)
        },
        gwpAmount (state, getters){ // Gift With Purchase Amount
            if (getters.isGwp) {
                // gift card list
                return '$100';
            }
            return ''
        },
        getGoogleAPISessionToken(state){
            if (state.googleAPI.places === null) return null;
            if (state.googleAPI.sessionToken === null || (state.googleAPI.sessionTokenCreated && dt.now().diff(dt.fromISO(state.googleAPI.sessionTokenCreated), 'minutes').toObject().minutes > 3)){
                state.googleAPI.sessionToken = new state.googleAPI.places.AutocompleteSessionToken();
                state.googleAPI.sessionTokenCreated = dt.now().toISO()
                return state.googleAPI.sessionToken
            }
            return state.googleAPI.sessionToken
        },
    },
    // Mutations are for mutating/changing state
    mutations: {
        SET_ENV_VARIABLES (state, value) {
            state.env = { ...state.env, ...value }
        },
        SET_ANALYTICS_MARKET(state, value){
            const troyZips = [
                "03301",
                "03303",
                "03820",
                "03823",
                "03824",
                "03825",
                "03867",
                "03874",
                "03878",
                "26501",
                "26505",
                "26508"
            ]

            if(state.meta.stateOrProvince === 'OH'){
                state.analyticsMarket = 'Ohio'
            } else if(troyZips.includes(state.meta.postalCode)) {
                state.analyticsMarket = 'Troy'
            } else {
                state.analyticsMarket = 'Legacy'
            }
        },
        SET_REFERRAL_CODE (state, value) {
            state.meta.referralCode = value
        },
        ADJUST_OPEN_OVERLAYS_COUNT_BY (state, value) {
            state.openOverlaysCount += value
        },
        ADJUST_LOADING_QUEUE_BY (state, value) {
            state.loadingQueue += value
        },
        SET_CLEARLINK_ID (state, payload) {
            state.clearlinkId = payload.clearlinkId
            state.clearlinkOfferId = payload.clearlinkOfferId
        },
        SET_PAGE_SCROLLABILITY (state, value) {
            if (value) {
                state.scrollPosition = [
                    document.documentElement.scrollLeft,
                    document.documentElement.scrollTop,
                ]

                if (state.scrollPositionLock === null) {
                    state.scrollPositionLock = () => {
                        scrollTo(...state.scrollPosition)
                    }
                    addEventListener('scroll', state.scrollPositionLock)
                }

            } else {
                removeEventListener('scroll', state.scrollPositionLock)
                state.scrollPosition = null
                state.scrollPositionLock = null
            }
        },
        SET_OFFERS (state, offers) {
            state.residentialOffers = offers
        },
        UPDATE_SELECTED_OFFER_IS_AVAILABLE (state, value) {
            state.selectedOfferIsAvailable = value
        },
        SET_CUSTOMER_STATE (state, value) {
            state.meta.customerState = value
        },
        UPDATE_CSG_ENVIRONMENT(state, value) {
            state.csgEnvironment = value
        },
        SET_PENDING_DISCONNECT(state, value) {
            state.meta.pendingDisconnect = value
        },
        SET_IS_LOADING (state, value) {
            state.isLoading = value
        },
        SET_LOADING_VISIBILITY (state, value) {
            state.loadingVisibility = value
        },
        SET_ACSID (state, value) {
            state.meta.acsid = value
        },
        SET_IMPACT_RADIUS_CLICK_ID (state, value) {
            state.meta.impactRadiusClickId = value
        },
        SET_POPUP (state, value) {
            state.popup = { ...state.popup, ...value }
        },
        SET_CHECK_AVAILABILITY_MODAL (state, value) {
            state.checkAvailabilityModal = { ...state.checkAvailabilityModal, ...value }
        },
        SET_BUSINESS_SELECT_PLAN_MODAL (state, value) {
            state.businessSelectPlanModal = { ...state.businessSelectPlanModal, ...value }
        },
        SET_METADATA (state, value) {
            state.meta = { ...state.meta, ...value }
        },
        SET_ERROR_STATUS (state, value) {
            state.meta.errorStatus = value
        },
        SET_AVAILABLE_OFFERS (state, { businessOffers }) {
            state.businessOffers = businessOffers
        },
        SET_SELECTED_OFFER (state, { selectedOffer }) {
            state.selectedOffer = selectedOffer
        },
        SET_FORCED_MARKET (state, value) {
            state.forcedMarket = { ...state.forcedMarket, ...value }
        },
        CREATE_CONVERSATION (state, value) {
            state.meta.createdConversation = value
        },
        RESET_PERSISTED_SESSION (state){
            // Called by Buyflow when session expires
            state.meta = {
                ...metaFactory(),
                postalCode: state.meta.postalCode,
            }
            state.ecommerceItem = ecommerceItemFactory()
            state.selectedOfferIsAvailable = true
            state.residentialOffers = []
            state.businessOffers = []
            state.Buyflow.tv.tvOptions.declined = false
            state.Buyflow.voice.voiceOptions.declined = false
            state.Buyflow.decisions = {}
            state.showTvNotAvailableModal = true
        },
        SET_ECOMMERCE_ITEM (state, { item }) {
            state.ecommerceItem = { ...state.ecommerceItem, ...item }
        },
        RESET_ECOMMERCE_ITEM (state) {
            state.ecommerceItem = ecommerceItemFactory()
        },
        RESET_MOBILE (state) {
            state.Buyflow.fromMobileLP = false
            state.Buyflow.mobileInterested = false
        },
        FROM_MOBILE_LP (state, payload) {
            state.Buyflow.fromMobileLP = payload
            state.Buyflow.mobileInterested = payload
        },
        SET_QUICK_ADDRESS_IN_USED (state, value) {
            state.quickAddressInUsed = value;
        },
        SET_QUICK_ADDRESS_IN_ERROR (state, value) {
            state.quickAddressInError = value;
        },
        SET_GOOGLE_API (state, value) {
            state.googleAPI = { ...state.googleAPI, ...value }
        },
        SET_GIFT_CARD_ELIGIBLE(state, value) {
            state.giftCardEligible = value
        },
        SET_SHOW_NO_TV_AVAILABLE_MODAL(state, value) {
          state.showTvNotAvailableModal = value
        },
    },

    actions: {
        setEnvVariables ({ commit, }, payload) {
            commit('SET_ENV_VARIABLES', payload)
        },
        autoZip ({ state, commit, dispatch }) {
            const queryString = window.location.search;

            if((queryString && new URLSearchParams(queryString).get('mt'))|| state.forcedMarket.marketType) {
                commit('SET_METADATA', metaFactory())
                return dispatch('fetchAvailableOffers','marketType')
            } else if (state.meta.postalCode === null){
                return dispatch('fetchAvailableOffers','autozip')
            }
        },
        adjustOpenOverlaysCounter ({ state, commit }, { value }) {
            commit('ADJUST_OPEN_OVERLAYS_COUNT_BY', value ? 1 : -1)
            commit('SET_PAGE_SCROLLABILITY', state.openOverlaysCount > 0)
        },
        setClearlinkId ({ commit }, payload) {
            commit('SET_CLEARLINK_ID', payload)
        },
        updateCsgEnv ({ state, commit }, value) {
            commit('UPDATE_CSG_ENVIRONMENT', value)
            axios.defaults.headers.common['X-Set-CSG-Environment'] = value
        },
        toggleLoading ({ state, commit }, { value }) {
            commit('ADJUST_LOADING_QUEUE_BY', value ? 1 : -1)
            commit('SET_IS_LOADING', state.loadingQueue > 0)
            commit('SET_PAGE_SCROLLABILITY', state.loadingQueue > 0)
        },
        setLoadingVisibilty ({ state, commit }, { value }) {
            commit('SET_LOADING_VISIBILITY', value)
        },
        setAcsid ({ state, commit }, value) {
            commit('SET_ACSID', value)
        },
        setImpactRadiusClickId ({ state, commit }, value) {
            commit('SET_IMPACT_RADIUS_CLICK_ID', value)
        },
        setQuickAddressInUsed({ state, commit }, { value }) {
            commit('SET_QUICK_ADDRESS_IN_USED', value)
        },
        setQuickAddressInError({ state, commit }, { value }) {
            commit('SET_QUICK_ADDRESS_IN_ERROR', value)
        },
        setForcedMarket ({ state, commit }, payload) {
            commit('SET_FORCED_MARKET', payload)
        },
        togglePopup({ commit, dispatch }, { type = null, error }) {
            commit('SET_POPUP', {
                isVisible: Boolean(type),
                type,
            })

            if (type?.indexOf("ERRORS") > -1) {
                // Send gtag event with custom error info
                mainMixins.sendEvent('javascript error', null, null, null, {
                    'error_message': error.message ?? '',
                    'page_url': window.location.href ?? '',
                })
            }

            dispatch('adjustOpenOverlaysCounter', { value: Boolean(type) })
        },
        toggleCheckAvailabilityModal ({ state, commit, dispatch }, payload) {
            if ((payload.visible && !state.checkAvailabilityModal.visible) || (!payload.visible && state.checkAvailabilityModal.visible)) {
                dispatch('adjustOpenOverlaysCounter', { value: payload.visible });
            }

            commit('SET_CHECK_AVAILABILITY_MODAL', payload)
        },
        toggleBusinessSelectPlanModal ({ commit, dispatch }, payload) {
            commit('SET_BUSINESS_SELECT_PLAN_MODAL', payload)
            dispatch('adjustOpenOverlaysCounter', { value: payload.visible })
        },
        setCustomerState({ commit, }, payload) {
            commit('SET_CUSTOMER_STATE', payload)
        },
        setGoogleAPI({ commit, }, payload) {
            commit('SET_GOOGLE_API', payload)
        },
        checkPendingDisconnect({ state, commit, dispatch }) {
            if (state.meta.locationId) {
                dispatch('toggleLoading', { value: true })
                return mainAPI._pendingDisconnect({ LocationId: state.meta.locationId, })
                    .then(response => {
                        if (response.pendingDisconnect !== null) {
                            commit('SET_PENDING_DISCONNECT', { ...response.pendingDisconnect, })
                        }
                    }).catch(error => {
                        console.error(error)
                    })
                    .finally(() => {
                        dispatch('toggleLoading', { value: false })
                    })
            }
        },
        checkZip({ commit, dispatch }, payload) {
            dispatch('toggleLoading', { value: true })
            return dispatch('fetchAvailableOffers', payload)
                .then(r => {
                    commit('SET_METADATA', {
                        line1: null,
                        line2: null,
                        country: null,
                        firstName: null,
                        uuid: null,
                        conversationId: null,
                        locationId: null,
                        billerMappingCode: null,
                        isCurrentCustomer: null,
                        customerState: null,
                    })
                    commit('BroadbandLabels/RESET_LABELS')
                    return r
                })
                .finally(() => {
                    dispatch('toggleLoading', { value: false })
                })
        },
        handleRoeRedirects({ commit, state, dispatch }, { response, payload }) {
            try {
                //clear all right of entry state
                commit('SET_METADATA', {
                    isRightOfEntry: false,
                    roeRedirect: false,
                    houseKey: null,
                    statusCode: null,
                    line1: null,
                    line2: null,
                    stateOrProvince: null,
                    city: null,
                    town: null,
                    postalCode: null,
                    dwellingType: null
                });


                // If no address alert, exit early
                if (!response?.address_alert) {
                    console.log('No address alert, exiting');
                    return Promise.resolve();
                }

                // Set offers array to empty
                this.commit('SET_OFFERS', [])
                        
                const address_alert = response.address_alert;
                const currentUrl = window.location.href;
                const hasGoFiber = currentUrl.toLowerCase().includes('gofiber');
                const alertType = address_alert.type?.toLowerCase();

                console.log('roe payload: ', payload)
                const metadata = {
                    isRightOfEntry: true,
                    houseKey: address_alert.house_key || null,
                    statusCode: address_alert.status_code || null,
                    line1: payload.line1 || null,
                    line2: payload.line2 || null,
                    stateOrProvince: payload.stateOrProvince || null,
                    city: payload.city || null,
                    town: payload.town || null,
                    postalCode: payload.postalCode || null,
                    dwellingType: address_alert.dwelling_type || null
                };

                dispatch('toggleLoading', { value: true })

                // Handle redirects for non-goFiber URLs
                if (!hasGoFiber) {
                    if (alertType === 'gofiber') {
                        // Set metadata
                        commit('SET_METADATA', {
                            ...metadata,
                            roeRedirect: true
                        });
                        console.log('Redirecting to goFiber');
                        window.location.replace('/gofiber');
                        return new Promise(() => {});
                    }
                    
                    if (alertType === 'vati') {
                        console.log('Redirecting to fiberiscoming');
                        window.location.replace('/fiberiscoming');
                        return new Promise(() => {});
                    }
                }
        
                // Handle goFiber case
                if (hasGoFiber) {
                    if (alertType === 'gofiber') {
                        // Set metadata
                        commit('SET_METADATA', metadata);
                        dispatch('toggleLoading', { value: false })
                        return Promise.resolve().then(() => rightOfEntry(state.meta, this));
                    }
                    
                    if (alertType === 'vati') {
                        console.log('Redirecting to fiberiscoming');
                        window.location.replace('/fiberiscoming');
                        return new Promise(() => {});
                    }
                }
        
            } catch (error) {
                console.error('Error in handleRoeRedirects:', error);
                return Promise.reject(error);
            }

            dispatch('toggleLoading', { value: false })
        },
        checkAddress ({ state, commit, dispatch, getters, rootState }, payload) {

            dispatch('toggleLoading', { value: true })
            commit('RESET_ECOMMERCE_ITEM')
            commit('RESET_MOBILE')
            const preMarketType = state.meta.marketType

            // if it exists, make sure we're saving email regardless of user path
            if (payload.emailAddress) {
                commit('SET_METADATA', { emailAddress: payload.emailAddress })
            }
            // true => check address, false => select address
            let type = payload?.billerMappingCode === undefined
            let promise = null

            if (type) {
                promise = mainAPI._checkAddress(payload)
            } else {
                promise = mainAPI._selectAddress({
                    ...payload,
                    ...payload.address,
                })
            }

            return promise.then(response => {
                dispatch('handleRoeRedirects', { 
                    response: response,
                    payload: payload
                });

                if (type && response.data.length){ // returns a list of addresses
                    return {
                        isValid: false,
                        suggestedAddresses: response.data,
                    }
                } else {
                    // The submitted address was valid and accepted
                    commit('SET_METADATA', response.meta)
                    commit('SET_OFFERS', response.offers)
                    commit('BroadbandLabels/RESET_LABELS')
                    commit('Buyflow/COMPLETE_ORDER', false)

                    if (!response.meta.isCurrentCustomer){
                        dispatch('setCustomerState', null)
                    }

                    if (response.meta.isCurrentCustomer && state.meta.customerState !== 'moving'){
                        dispatch('setCustomerState', 'current')

                        dispatch('toggleCheckAvailabilityModal', {
                            currentCustomerCheck: true,
                            visible: true,
                        })

                        return {
                            isValid: false,
                        }
                    } else if (response.meta.conversationId) {
                        dispatch('Personalization/ping', {
                            eventName: 'CHECK_AVAILABILITY',
                            payload: {
                                user: {
                                    identities: {
                                        houseKeyNew: response.meta.locationId,
                                        emailAddress: payload.emailAddress || '',
                                    },
                                    attributes: {
                                        houseKeyNew: response.meta.locationId,
                                        emailAddress: payload.emailAddress || '',
                                        street: response.meta.line1,
                                        apt: response.meta.line2,
                                        city: response.meta.city,
                                        state: response.meta.stateOrProvince,
                                        zip: response.meta.postalCode.substring(0, 5),
                                        country: 'USA',
                                    },
                                },
                            },
                        })

                        dataLayer.push({
                            'event': 'addressSubmission',
                            'addressValidity': 'new customer',
                            'cookie_state': state.meta.stateOrProvince || null,
                            'cookie_zip_code': state.meta.postalCode? state.meta.postalCode.substring(0, 5): null,
                        })

                        // this is the customer uuid in GA
                        gtag('event', 'custom_dimension', {
                            'dimension11': response.meta.uuid
                        });

                        commit('SET_ANALYTICS_MARKET', state.meta.postalCode)

                        mainMixins.sendEvent(`Verify Address`,
                                'New Customer',
                                `Region:${state.meta.stateOrProvince}
                                - Zip Code:${state.meta.postalCode? state.meta.postalCode.substring(0, 5): 'Not Available'}
                                - City:${state.meta.city? state.meta.city.toLowerCase(): null}
                                - Market:${state.analyticsMarket}`)

                        return {
                            isValid: true
                        }
                    }
                }
            })
                .then(response => {
                    if (response?.isValid) {
                        return dispatch('fetchAvailableOffers', state.meta.postalCode)
                            .then(() => {
                                commit('CREATE_CONVERSATION', dt.now().toISO())
                                return {
                                    isValid: true,
                                    changedMarket: state.meta.marketType !== preMarketType,
                                }
                            }).then(() => {
                                if (state.checkAvailabilityModal.hasSelectedGiftCard) {
                                    dispatch(
                                        'toggleCheckAvailabilityModal',
                                        { hasSelectedGiftCard: false }
                                    )

                                    dispatch('handleGiftCardEligibility')
                                }
                            })
                    }

                    return response
                })
                .catch(error => {
                    if (error?.meta?.code) {
                        this.captureMessage(error.meta.code);
                    }

                    // error status for checkAvailabilityReporting function
                    const errorStatus = error.status;
                    if(errorStatus) {
                        commit('SET_ERROR_STATUS', errorStatus);
                    }

                    commit('SET_METADATA', {
                        ...error.meta,
                    });

                    dataLayer.push({
                        'event': 'addressSubmission',
                        'addressValidity': 'failed address',
                        'cookie_state': payload.stateOrProvince || null,
                        'cookie_zip_code': payload.postalCode || null,
                    })

                    commit('SET_ANALYTICS_MARKET', payload.postalCode)

                    mainMixins.sendEvent(
                        'Verify Address', 
                        'Failed Address', 
                        `Region:${payload.stateOrProvince} - Zip Code:${payload.postalCode} - City:${payload.city? payload.city.toLowerCase(): null} - Market: ${state.analyticsMarket}`
                    )

                    throw {
                        status: error?.message || '',
                        displayModal: error?.meta?.display_modal || '',
                        errorMessage: error?.errors?.length ? error?.errors[0] : '',
                        increaseAttempts: error?.meta?.failedAttempt,
                    }
                })
                .finally(() => {
                    dispatch('toggleLoading', { value: false })
                    return
                })
        },


        handleGiftCardEligibility({state, dispatch}) {
            if (state.giftCardEligible !== false && state.residentialOffers.length === 0) {
                return
            }

            if (state.giftCardEligible?.events?.length > 0) {
                state.giftCardEligible?.events.map(event => gtag('event', event))
            }

            const availableOffer = state.residentialOffers.find(
                offer => state.giftCardEligible?.offers?.includes(offer.name)
            )


            if (availableOffer) {
                availableOffer.selected['GC1 Gift Card'] = { name: 'Gift Card' }

                dispatch('setSelectedOffer', { selectedOffer: availableOffer })
                .then(() => {
                    dispatch('selectOffer', true)
                })
            }
        },
        reinitializeSession ({ state, commit, dispatch }, payload) {
            const userFields = {
                firstName: payload.firstName,
                lastName: payload.lastName,
                emailAddress: payload.emailAddress,
                phoneNumber: payload.phoneNumber
            }

            return mainAPI._selectAddress({
                ...payload,
                ...payload.address,
            })
                .then(response => {
                    commit('SET_METADATA', {
                        ...response.meta,
                        ...userFields
                    })
                    commit('SET_METADATA', { isAbandonedCart: true })
                    commit('SET_OFFERS', response.offers)
                    commit('Buyflow/COMPLETE_ORDER', false)
                    commit('SET_ANALYTICS_MARKET', state.meta.postalCode)

                    if (response.meta.isCurrentCustomer && state.meta.customerState !== 'moving') {
                        dispatch('setCustomerState', 'current')
                        throw 'already a customer'
                    } else if (response.meta.conversationId) {
                        dataLayer.push({
                            'event': 'addressSubmission',
                            'addressValidity': 'new customer',
                            'cookie_state': state.meta.stateOrProvince || null,
                            'cookie_zip_code': state.meta.postalCode ? state.meta.postalCode.substring(0, 5) : null,
                        })

                        mainMixins.sendEvent('Verify Address', 'New Customer', `Region:${state.meta.stateOrProvince} - Zip Code:${state.meta.postalCode ? state.meta.postalCode.substring(0, 5) : 'Not Available'} - City:${state.meta.city? state.meta.city.toLowerCase(): null} - Market: ${state.analyticsMarket}`)
                        return dispatch('Buyflow/initialize')
                    }
                })
                .then(() => {
                    return mainAPI._verifyAddressAndSelectProduct({
                        uuid: state.meta.uuid,
                        conversationId: state.meta.conversationId,
                        billerMappingCode: state.meta.billerMappingCode,
                        locationId: state.meta.locationId,
                        verifyAddress: false,
                        decisions: state.selectedOffer.selected,
                    })
                        .then(response => {
                            commit('SET_METADATA', {
                                ...response?.meta,
                                billingLine1: "",
                                billingLine2: "",
                                billingCity: "",
                                billingStateOrProvince: "",
                                billingPostalCode: "",
                                referralCodeUsed: false,
                                isAbandonedCart: true
                            })
                            dispatch("Buyflow/_formatCartData", response.data.receipt)
                                .then(cartData => dispatch('Buyflow/receipt/updateCart', cartData))
                                .then(() => dispatch('Buyflow/tracking/updatePersonalizationCart'))

                            commit("Buyflow/SET_DECISIONS", Object.fromEntries(Object.entries(response?.meta?.decisionTree ?? {}).filter(([_, v]) => v != null)))
                            commit('SET_FORCED_MARKET', {
                                marketType: null,
                                showPopup: false,
                            })

                            // if the selected offer was not available
                            if (state.selectedOffer?.name && response?.meta?.decisionTree['HS1 HSD Service']?.name !== state.selectedOffer?.name) {
                                commit('UPDATE_SELECTED_OFFER_IS_AVAILABLE', false)
                            }

                            return dispatch('Buyflow/initialize')
                        })
                        .then(() => {
                            return {
                                isValid: true,
                            }
                        })
                        .catch(() => {
                            throw 'offer selection error'
                        })
                })
                .catch(error => {
                    dataLayer.push({
                        'event': 'addressSubmission',
                        'addressValidity': 'failed address',
                        'cookie_state': payload.address.stateOrProvince || null,
                        'cookie_zip_code': payload.address.postalCode || null,
                    })

                    mainMixins.sendEvent('Verify Address', 'Failed Address', `Region:${payload.address.stateOrProvince} - Zip Code:${payload.address.postalCode} - City:${payload.address.city? payload.address.city.toLowerCase(): null} - Market: Unserviceable`)
                    throw error
                })
                .finally(() => {
                    gtag("event", "begin_checkout", {
                        currency: "USD",
                        value: state.ecommerceItem.price,
                        items: [ state.ecommerceItem ],
                    })
                })
        },
        setMeta ({ commit }, payload) {
            commit('SET_METADATA', payload)
        },
        setLabels ({ commit }, payload) {
            commit('BroadbandLabels/RESTORE_LABELS', payload)
        },
        fetchAvailableOffers ({ state, commit, dispatch, getters }, payload) {
            dispatch('toggleLoading', { value: true })
            let params = {}
            if (payload !== 'autozip') {
                if (payload === 'marketType') {
                    const queryString = window.location.search;
                    const marketType  = new URLSearchParams(queryString).get('mt')?.replace(/[^a-zA-Z\d-]/g, '')

                    if(queryString && marketType) {
                        commit('SET_FORCED_MARKET', {
                            marketType,
                            showPopup: false,
                        })
                        commit('SET_METADATA', {
                            marketType
                        })
                        params = {
                            offer: marketType
                        }
                    } else if(state.forcedMarket.marketType) {
                        commit('SET_METADATA', {
                            marketType: state.forcedMarket.marketType
                        })
                        params = {
                            offer: state.forcedMarket.marketType
                        }
                    } else {
                        params = {
                            offer: 'default'
                        }
                    }
                } else if (payload || state.meta.postalCode) {
                    let zipcode = payload || state.meta.postalCode
                    params = {
                        zip_code: zipcode.substring(0,5),
                    }
                } else {
                    params = {
                        offer: 'default'
                    }
                }
            }
            // this endpoint needs the data flattened this way
            return mainAPI._fetchAvailableOffers(params)
                .then(r => r.data)
                .then(r => {
                    const location             = r.location
                    const businessOffers       = r.businessOffers.length ? r.businessOffers[0] : []
                    const channels             = r.channels

                    const offerList = {
                        businessOffers: businessOffers,
                        location,
                    }
                    if(location.city || !state.meta.postalCode){
                        commit('SET_AVAILABLE_OFFERS', {businessOffers})
                    }

                    if (location.city) {
                        if(state.forcedMarket.marketType && baseOffers.name && baseOffers.name !== state.forcedMarket.marketType ) {
                            commit('SET_FORCED_MARKET', {
                                showPopup: true,
                            })
                        }

                        commit('SET_METADATA', {
                            town: location.town || null,
                            stateOrProvince: location.stateID || null,
                            postalCode: location.zipCode || null,
                            channelLineup: location.channelLineup || null,
                            businessPromotion: location.promotion || null,
                            features: location.features || null,
                            channels: channels,
                            rateCard: location.rateCard,
                            sites: location.sites || null
                        })
                    }

                    return offerList
                })
                .catch(error => {
                    console.log(error)
                })
                .finally(() => {
                    dataLayer.push({'event': 'optimize.activate'});
                    dispatch('toggleLoading', { value: false })
                    if (payload === 'autozip' || state.checkAvailabilityModal.resetSelectedOffer === true) {
                        commit('SET_ANALYTICS_MARKET', state.meta.postalCode)
                        gtag( 'event', 'zipped', {
                            'event_category': 'zipped in',
                            'event_action': state.meta.marketType,
                            'event_label': `Region: ${state.meta.stateOrProvince} - Zip Code: ${state.meta.postalCode} - City: ${state.meta.city} - Market: ${state.analyticsMarket}`,
                            'non_interaction': true,
                        })
                    }
                })
        },
        setSelectedOffer({ commit, dispatch }, payload){
            commit('SET_SELECTED_OFFER', payload)
            dispatch('Buyflow/setReturnUrl', (window.location.pathname + window.location.search))
        },
        setEcommerceItem({ commit }, payload){
            commit('SET_ECOMMERCE_ITEM', payload)
        },
        setReferralCode({ commit }, payload){
            commit('SET_REFERRAL_CODE', payload)
        },
        selectOffer({ state, commit, dispatch }, verifyAddress = true ) {
            dispatch('toggleLoading', { value: true })
            let goToCheckout = false
            const decisions  = {
                "IF1 Install - Fulfillment": {
                    "name": "Professional Install"
                },
                ...state.selectedOffer.selected ?? {},
            }

            // If customer checks address and is valid, and within 5 minutes selects an offer, don't revalidate the address for serviceability as it'll take an additional 2-3 seconds
            if (state.meta.createdConversation) {
                let diff = dt.now().diff(dt.fromISO(state.meta.createdConversation), 'minutes')
                if (diff.toObject().minutes <= 5) {
                    verifyAddress = false
                }
            }
            commit('CREATE_CONVERSATION', null) //TODO change to set conversation instead of create

            // if verifyAddress is true then reverify the current address for serviceability, then add the selected offer to the cart
            mainAPI._verifyAddressAndSelectProduct({
                uuid: state.meta.uuid,
                conversationId: state.meta.conversationId,
                billerMappingCode: state.meta.billerMappingCode,
                locationId: state.meta.locationId,
                verifyAddress: verifyAddress,
                decisions,
            })
                .then(response => {
                    commit('SET_METADATA', {
                        ...response?.meta,
                        firstName: "",
                        lastName: "",
                        phoneNumber: "",
                        billingLine1: "",
                        billingLine2: "",
                        billingCity: "",
                        billingStateOrProvince: "",
                        billingPostalCode: "",
                        referralCodeUsed: false,
                    })
                    dispatch("Buyflow/_formatCartData", response.data.receipt)
                        .then(cartData => dispatch('Buyflow/receipt/updateCart', cartData))
                        .then(() => dispatch('Buyflow/tracking/updatePersonalizationCart'))
                    commit("Buyflow/SET_DECISIONS", Object.fromEntries(Object.entries(response?.meta?.decisionTree ?? {}).filter(([_, v]) => v != null)))
                    commit('SET_FORCED_MARKET', {
                        marketType: null,
                        showPopup: false,
                    })

                    // if the selected offer was not available
                    if (state.selectedOffer?.name && response?.meta?.decisionTree['HS1 HSD Service']?.name !== state.selectedOffer?.name) {
                        commit('UPDATE_SELECTED_OFFER_IS_AVAILABLE', false)
                    }

                    // If the API call was successful, allow the selection to go to checkout
                    goToCheckout = true

                    return dispatch('Buyflow/initialize')
                })
                .catch(error => {
                    if (error.errors && error.errors.length && error.errors[0]){
                        throw {
                            errorMessage: error.errors[0],
                        }
                    } else {
                        console.log(error)
                    }
                })
                .finally(() => {
                    gtag("event", "begin_checkout", {
                        currency: "USD",
                        value: state.ecommerceItem.price,
                        items: [ state.ecommerceItem ],
                    })
                    window.onpagehide = function() {
                        dispatch('toggleLoading', { value: false })
                    }
                    // If the API call was successful, go to checkout
                    if (goToCheckout) {
                        setTimeout(() => {
                            window.location = '/checkout'
                        }, 500)
                    } else {
                        // if the customer's address doesn't have all the required information, show the check availability modal
                        dispatch('toggleLoading', { value: false })
                        dispatch('toggleCheckAvailabilityModal',{
                            visible: true,
                            routeToBuyflow: true,
                            resetSelectedOffer: false,
                            currentCustomerCheck: false,
                        })
                    }
                })
        },
        setAbandonedCartGiftCardTrigger({ commit }, trigger) {
            commit('SET_METADATA', { abandonedCart: { giftCardTrigger: trigger } })
        },
        setShowNoTvAvailableModal({ commit }, value) {
          commit('SET_SHOW_NO_TV_AVAILABLE_MODAL', value)
      },
    },

    modules: {
        Buyflow: BuyflowStore,
        orderRemote: OrderRemoteStore,
        Personalization: PersonalizationStore,
        BroadbandLabels: LabelStore,
        highlights: HighlightStore,
        Promotions: PromotionStore,
        resources: new ElementApiModule('/resources.json', { debounce: 1000 }),
        resourceTypes: new ElementApiModule('/resourceTypes.json', { debounce: 1000 }),
        resourceIndustries: new ElementApiModule('/resourceIndustries.json', { debounce: 1000 }),
        resourceProducts: new ElementApiModule('/resourceProducts.json', { debounce: 1000 }),
    },
})
